import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Box, Modal, Alert, LinearProgress, InputAdornment, IconButton, TextField, Snackbar, Divider } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Visibility, VisibilityOff, Add, Delete, Save } from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchWrapperNoAuth, SERVER } from '../../utils/helpers';
import ErrorModal from '../../components/Modals/ErrorModal';

const StyledTextField = styled(TextField)(({ theme, customtheme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: customtheme.color.primary,
    },
    "&:hover fieldset": {
      borderColor: customtheme.color.secondary,
    },
    "&.Mui-focused fieldset": {
      borderColor: customtheme.color.primary,
    },
  },
  "& .MuiInputLabel-root": {
    color: customtheme.color.primary,
    fontFamily: customtheme.fontFamily,
    "&.Mui-focused": {
      color: customtheme.color.primary,
    },
  },
  "& .MuiOutlinedInput-input": {
    fontFamily: customtheme.fontFamily,
    color: customtheme.text.primary,
  },
  "& .MuiInputLabel-outlined.Mui-error": {
    color: customtheme.accent.error,
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: customtheme.accent.error,
  },
}));

const CustomInput = ({ type, ...props }) => {
  const { theme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <StyledTextField
      {...props}
      type={type === "password" && showPassword ? "text" : type}
      size="small"
      slotProps={{
        input: {
          ...props.InputProps,
          endAdornment:
            type === "password" ? (
              <InputAdornment position="end">
                <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : null,
        }
      }}
      customtheme={theme}
    />
  );
};

const Account = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, password, encodedAuth, userData, setUserData } = useAuth();  // userData
  const { theme } = useTheme();

  const { error } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [nextBillingTime, setNextBillingTime] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [openErrorModal, setOpenErrorModal] = useState(!!error);

  // New state variables for user profile
  const [firstName, setFirstName] = useState(userData?.firstName || "");
  const [lastName, setLastName] = useState(userData?.lastName || "");
  const [addresses, setAddresses] = useState(
    userData?.addresses && Array.isArray(userData.addresses)
      ? userData.addresses
      : [{
        addressLine: "",
        city: "",
        stateCode: "",
        countryCode: "",
        postalCode: ""
      }]
  );
  const [phone, setPhone] = useState(userData?.phone || "");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  useEffect(() => {
    // Get current URL search params
    const urlParams = new URLSearchParams(window.location.search);

    // Check if walmartCallbackUri parameter exists
    if (urlParams.has('walmartCallbackUri')) {
      handleConnectToWalmart();
    }
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  useEffect(() => {
    const getAccountDetails = async () => {
      setLoading(true);
      try {
        // Fetch subscription details
        const subResponse = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details/${user}/`, user, password);
        setActive(subResponse.active);
        setNextBillingTime(subResponse.next_billing_time);

        // Fetch user profile details
        const userResult = await fetchWrapperNoAuth(`${SERVER}/falcon_get_user/${user}`, user, password);

        setFirstName(userResult.first_name || "");
        setLastName(userResult.last_name || "");

        // Ensure addresses is an array of objects
        const fetchedAddresses = typeof userResult.address_list === 'string'
          ? JSON.parse(userResult.address_list)
          : [{
            addressLine: "",
            city: "",
            stateCode: "",
            countryCode: "",
            postalCode: ""
          }];
        setAddresses(fetchedAddresses);

        setPhone(userResult.phone || "");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    getAccountDetails();
  }, []);

  const handleKeyDown = (event, tab) => {
    if (event.key === 'Enter') {
      if (!tab) {
        saveAddresses();
      } else {
        event.preventDefault(); // Prevent the default Enter behavior

        // Find the next focusable element
        const focusableElements = document.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const currentIndex = Array.prototype.indexOf.call(
          focusableElements,
          document.activeElement
        );

        if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
          const nextElement = focusableElements[currentIndex + 1];
          nextElement.focus(); // Focus the next element
        }
      }
    }
  };

  const handleProceed = async () => {
    try {
      const result = await fetchWrapperNoAuth(`${SERVER}/falcon_cancel_subscription/${user}/`, user, password);
      if (result) {
        navigate('/');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleResetKeys = async (e) => {
    setResetLoading(true);
    const body = { email: user };
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_remove_secret_keys`, user, password, { method: 'POST', body: body });
      localStorage.removeItem("IFawa");
      window.location.reload();
    } catch (error) {
      console.error('Error updating secret keys', error);
    } finally {
      setResetLoading(false); // Reset loading state if needed (or remove this if page reloads)
    }
  }

  function handleConnectToWalmart() {
    if (user == null) {
      window.location.href = "/login?walmartCallbackUri=https://login.account.wal-mart.com/authorize"
    } else {
      window.location.href = `https://login.account.wal-mart.com/authorize?responseType=code&clientId=ed466fe8-fc58-4779-8315-a23b64d4de56&redirectUri=https://www.inventoryfalcon.com/&nonce=${Array.from({ length: 10 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 62)]).join('')}&state=${user}&clientType=seller`
    }
  }

  const addNewAddress = () => {
    setAddresses([
      ...addresses,
      {
        addressLine: "",
        city: "",
        stateCode: "",
        countryCode: "",
        postalCode: ""
      }
    ]);
  };

  const updateAddress = (index, field, value) => {
    const newAddresses = [...addresses];
    newAddresses[index][field] = value;
    setAddresses(newAddresses);
  };

  const removeAddress = async (index) => {
    const newAddresses = addresses.filter((_, i) => i !== index);
    setUserData({ ...userData, addresses: newAddresses });

    const body = { email: user, addresses: newAddresses };
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_addresses`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error updating addresses', error);
    }

    // Ensure at least one address remains
    setAddresses(newAddresses.length > 0 ? newAddresses : [{
      addressLine: "",
      city: "",
      stateCode: "",
      countryCode: "",
      postalCode: ""
    }]);
    setSnackbarMsg("Addresses Removed!");
    setOpenSnackbar(true);
  };

  const saveAddresses = async () => {
    const body = { email: user, addresses: addresses };
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_addresses`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error updating addresses', error);
    }

    setUserData({ ...userData, addresses: addresses });
    setSnackbarMsg("Addresses Saved!");
    setOpenSnackbar(true);
  }

  const updateUser = async () => {
    const body = {
      email: user,
      firstName,
      lastName,
      addresses,
      phone
    };
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_user`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error updating user', error);
    }
  };

  const boxStyle = {
    mt: 4,
    p: '20px',
    overflowY: 'auto',
    maxHeight: '60vh',
    '&::-webkit-scrollbar': {
      width: '10px', // Width of the scrollbar
      height: '10px', // Width of the scrollbar
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.background.primary, // Track color
      borderRadius: '3px', // Optional: rounded corners
      border: `1px solid ${theme.border.light}`,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.scrollbar, // Thumb color
      borderRadius: '15px', // Optional: rounded corners
      cursor: 'grab',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.scrollbarHover, // Thumb color on hover
    },
    '&::-webkit-scrollbar-button': {
      display: 'none', // Thumb color on hover
    },
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: theme.background.primary, }}>
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom align="center"
            style={{ fontFamily: theme.fontFamily, color: theme.color.primary }}>
            Hey {user}!
          </Typography>
        </Box>

        {loading ? (
          <div style={{ color: theme.color.primary }}>
            <LinearProgress sx={{ marginTop: '16px' }} color={'inherit'} />
          </div>
        ) : (
          <Box sx={{ mt: 6, }}>
            <Box sx={{ textAlign: 'center' }}>
              {active ? (
                <Alert severity="success">Your account is Active</Alert>
              ) : (
                <Alert severity="error" action={
                  <Button
                    variant="outlined"
                    style={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.accent.success,
                      borderRadius: '20px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.accent.success}`,
                    }}
                    size="small"
                    onClick={() => {
                      window.location.href = `https://store.payproglobal.com/checkout?products%5B1%5D%5Bid%5D=108820&page-template=21043&language=en&currency=USD&billing-email=${user}`;
                    }}>
                    Activate Account
                  </Button>
                }>Your account is inactive</Alert>
              )}

              {active && (
                <Box sx={{ my: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color={theme.text.secondary}
                    style={{ fontFamily: theme.fontFamily, marginTop: 15 }}>
                    Your next billing date is: {nextBillingTime ? new Date(nextBillingTime).toLocaleDateString() : 'N/A'}
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={() => setOpenConfirmModal(true)}
                    style={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.accent.error,
                      borderRadius: '20px',
                      border: `1px solid ${theme.border.light}`,
                    }}
                  >
                    Cancel Subscription
                  </Button>
                </Box>
              )}
            </Box>

            <Box sx={boxStyle}>
              <Grid container spacing={2}>
                {(!encodedAuth || encodedAuth === "" || encodedAuth === "null") ? (
                  <>
                    <Grid item offset={3} size={6}>
                      <Button variant='contained' sx={{ width: '100%' }} onClick={handleConnectToWalmart} disabled={resetLoading}>Connect Your Walmart Account</Button>
                    </Grid>
                  </>
                ) : (
                  <Grid item offset={3} size={6}>
                    <Button sx={{ width: '100%' }} onClick={handleResetKeys} disabled={resetLoading}>{resetLoading ? "Loading..." : "Disconnect your Walmart account"}</Button>
                  </Grid>
                )}
                <Grid item size={6}>
                  <CustomInput
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item size={6}>
                  <CustomInput
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                {/* Addresses Section */}
                {addresses.map((address, index) => (
                  <React.Fragment key={index}>
                    <Grid item size={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1, fontFamily: theme.fontFamily, color: theme.text.primary }}>
                          Address {index + 1}
                        </Typography>
                        {addresses.length > 1 && (
                          <IconButton onClick={() => removeAddress(index)} color="error">
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                    <Grid item size={12}>
                      <CustomInput
                        fullWidth
                        label="Address Line"
                        value={address.addressLine}
                        onChange={(e) => updateAddress(index, 'addressLine', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                      />
                    </Grid>
                    <Grid item size={4}>
                      <CustomInput
                        fullWidth
                        label="City"
                        value={address.city}
                        onChange={(e) => updateAddress(index, 'city', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                      />
                    </Grid>
                    <Grid item size={2}>
                      <CustomInput
                        fullWidth
                        label="State"
                        value={address.stateCode}
                        onChange={(e) => updateAddress(index, 'stateCode', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                      />
                    </Grid>
                    <Grid item size={2}>
                      <CustomInput
                        fullWidth
                        label="Country"
                        value={address.countryCode}
                        onChange={(e) => updateAddress(index, 'countryCode', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                      />
                    </Grid>
                    <Grid item size={4}>
                      <CustomInput
                        fullWidth
                        label="Postal Code"
                        value={address.postalCode}
                        onChange={(e) => updateAddress(index, 'postalCode', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                      />
                    </Grid>
                  </React.Fragment>
                ))}

                {/* Add Address Button */}
                <Grid item size={4} offset={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addNewAddress}
                    sx={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.color.primary,
                      borderRadius: '20px',
                      borderColor: theme.color.primary,
                      '&:hover': {
                        backgroundColor: `${theme.color.primary}10`, // Slight background on hover
                      }
                    }}
                  >
                    Add Another Address
                  </Button>
                </Grid>
                <Grid item size={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={saveAddresses}
                    sx={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.accent.success,
                      borderRadius: '20px',
                      borderColor: theme.accent.success,
                      '&:hover': {
                        backgroundColor: `${theme.accent.success}10`, // Slight background on hover
                      }
                    }}
                  >
                    Save Addresses
                  </Button>
                </Grid>

                <Grid item size={12}>
                  <CustomInput
                    fullWidth
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Grid>

                <Grid item size={6} offset={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={updateUser}
                    sx={{
                      textTransform: 'none',
                      fontFamily: theme.fontFamily,
                      color: theme.button.color,
                      borderRadius: '20px',
                      bgcolor: theme.accent.success,
                    }}
                  >
                    Update User Information
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ zIndex: 99999999999 }}>
          <Alert
            onClose={handleClose}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>

        <Modal open={openConfirmModal} onClose={() => setOpenConfirmModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '20vw',
              bgcolor: theme.background.primary,
              borderRadius: 2,
              border: `4px solid ${theme.color.primary}`,
              boxShadow: 24,
              p: 4,
              outline: 'none',
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom
              style={{ fontFamily: theme.fontFamily, textAlign: 'center' }}>
              Are you sure you want to proceed?
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
              <Button variant="contained" onClick={() => setOpenConfirmModal(false)}
                style={{
                  textTransform: 'none',
                  fontFamily: theme.fontFamily,
                  color: theme.button.color,
                  borderRadius: '20px',
                  border: `1px solid ${theme.button.color}`,
                  backgroundColor: theme.accent.success
                }}>
                No
              </Button>
              <Button variant="contained" onClick={handleProceed}
                style={{
                  textTransform: 'none',
                  fontFamily: theme.fontFamily,
                  color: theme.button.color,
                  borderRadius: '20px',
                  border: `1px solid ${theme.button.color}`,
                  backgroundColor: theme.accent.error
                }}>
                Yes
              </Button>
            </Box>
          </Box>
        </Modal>
      </Container>
      <ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={error ?? ""} />
    </Box>
  );
};

export default Account;